import { useRef, useState } from "react";

/**
 * Drag-to-reorder interaction state for the placed dashboard grid.
 *
 * This hook owns only the transient drag UI (which card is being dragged,
 * which cell it would land on) and the native HTML5 drag-and-drop wiring. It
 * does NOT own any placement — on drop it calls `onDrop(fromId, cell)`, so the
 * URL-backed card list stays the single source of truth.
 *
 * The drop target is a grid CELL (row/col in rendered coordinates, plus which
 * section's grid it belongs to): any cell — an existing card OR an empty slot
 * — can be a target. The hovered cell is exposed as `hoverCell` so the UI can
 * preview the dragged card's whole footprint under the pointer.
 *
 * The drag also remembers its GRAB OFFSET — which 0-based sub-cell of the
 * card the pointer held at dragstart, in rendered cells, computed from the
 * drag source's bounding rect and the card's rendered span (pass it to
 * `dragHandlers`; without a span the offset is 0,0). It's exposed as
 * `grabOffset` and passed to `onDrop`, so the caller can keep the grabbed
 * cell — not the card's top-left — under the pointer for both the footprint
 * preview and the drop itself.
 *
 * Card drags stamp a custom dataTransfer type ({@link CARD_DRAG_TYPE}), and
 * every drop handler ignores gestures without it — so section drags, OS file
 * drags and text selections can never set the hovered cell or commit a card
 * move, independent of React state timing (same convention as
 * `SECTION_DRAG_TYPE` in `useSectionReorder`).
 *
 * Releasing over an ACCEPTING surface that has no drop handler of its own
 * (e.g. the grid gaps, which the consumer accepts via a grid-level dragover)
 * still commits: the footprint preview is showing at the last hovered cell,
 * so the source's `dragend` invokes `onDrop` with that cell when no real
 * `drop` handled the gesture. The caller's own fit check rejects unsafe
 * landings, so an invalid last hover (grey preview) safely no-ops. A
 * CANCELLED drag — Escape, or a release nothing accepted — reports
 * `dropEffect === "none"` at dragend, and the pending move is discarded.
 */

/** Custom dataTransfer type stamped on card drags, the card-level sibling of
 *  `SECTION_DRAG_TYPE`. `types` (not values) is readable during dragover, so
 *  drop targets can discriminate card drags from section/OS drags in
 *  protected mode. */
export const CARD_DRAG_TYPE = "application/x-roboto-card";

/** Where a drop would land: a rendered-grid cell of one section's canvas. */
export interface DropCell {
  row: number;
  col: number;
  section: string;
}

/** Which sub-cell of the dragged card the pointer grabbed (0-based, in
 *  rendered cells): `{rows: 0, cols: 0}` is the card's top-left cell. */
export interface GrabOffset {
  rows: number;
  cols: number;
}

const ZERO_OFFSET: GrabOffset = { rows: 0, cols: 0 };

export interface DragHandlers {
  draggable: true;
  onDragStart: (e: React.DragEvent) => void;
  onDragEnd: (e: React.DragEvent) => void;
}

/** True when the active drag carries the card discriminator type. */
function isCardDrag(e: React.DragEvent): boolean {
  return Array.from(e.dataTransfer.types).includes(CARD_DRAG_TYPE);
}

export interface DropHandlers {
  onDragEnter: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
}

export interface PlacedReorder {
  /** id of the card currently being dragged, or null. */
  draggingId: string | null;
  /** cell currently hovered, or null (drives the footprint preview). */
  hoverCell: DropCell | null;
  /** sub-cell of the dragged card the pointer grabbed at dragstart. */
  grabOffset: GrabOffset;
  /** Props making the element for `id` draggable (the drag SOURCE); pass the
   *  card's rendered span so the grab offset can be measured. */
  dragHandlers: (
    id: string,
    span?: { rows: number; cols: number },
  ) => DragHandlers;
  /** Props making a cell a drop TARGET. */
  dropHandlers: (cell: DropCell) => DropHandlers;
}

export function usePlacedReorder(
  onDrop: (fromId: string, cell: DropCell, grabOffset: GrabOffset) => void,
): PlacedReorder {
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [hoverCell, setHoverCell] = useState<DropCell | null>(null);
  const [grabOffset, setGrabOffset] = useState<GrabOffset>(ZERO_OFFSET);
  // Whether a real `drop` event committed the current gesture. Guards the
  // dragend fallback below against double-committing a handled drop (dragend
  // always follows drop, and may see stale state from before `clear`).
  const dropHandledRef = useRef(false);
  // Generation counter for the rAF-deferred dragstart state update below: a
  // drag that ends before its deferred frame runs (instant press-release)
  // must not resurrect the dragging state after `clear` — bumping the
  // generation in `clear` invalidates any still-pending callback.
  const gestureRef = useRef(0);

  function clear() {
    gestureRef.current += 1;
    setDraggingId(null);
    setHoverCell(null);
    setGrabOffset(ZERO_OFFSET);
  }

  function dragHandlers(
    id: string,
    span?: { rows: number; cols: number },
  ): DragHandlers {
    return {
      draggable: true,
      onDragStart: (e) => {
        e.dataTransfer.effectAllowed = "move";
        // Some browsers require data to be set for the drag to initiate; the
        // custom type is the card-drag discriminator drop targets key off.
        e.dataTransfer.setData("text/plain", id);
        e.dataTransfer.setData(CARD_DRAG_TYPE, id);
        // Which sub-cell of the card the pointer grabbed, measured NOW —
        // the source's rect is only trustworthy before the grid re-renders
        // mid-drag.
        let offset = ZERO_OFFSET;
        if (span) {
          const rect = e.currentTarget.getBoundingClientRect();
          const clamp = (v: number, max: number) =>
            Math.min(Math.max(v, 0), max);
          offset = {
            rows: clamp(
              Math.floor((e.clientY - rect.top) / (rect.height / span.rows)),
              span.rows - 1,
            ),
            cols: clamp(
              Math.floor((e.clientX - rect.left) / (rect.width / span.cols)),
              span.cols - 1,
            ),
          };
        }
        // Defer the state update to the next frame. Changing the DOM
        // synchronously inside `dragstart` (empty cells appear, resize handles
        // hide, cards reposition) can detach the drag source and CANCEL the
        // native drag. Letting dragstart finish first lets the drag commit;
        // the grid then updates a frame later, mid-drag.
        dropHandledRef.current = false;
        const gesture = (gestureRef.current += 1);
        requestAnimationFrame(() => {
          if (gestureRef.current !== gesture) {
            // The drag already ended (dragend ran `clear` before this frame).
            return;
          }
          setDraggingId(id);
          setGrabOffset(offset);
        });
      },
      onDragEnd: (e) => {
        // dropEffect "none" means the drag was CANCELLED — Escape, or a
        // release nothing accepted — so the pending move is discarded.
        // Otherwise, released over an accepting surface without a drop
        // handler (no `drop` fired) while the footprint preview was showing
        // at the last hovered cell: commit there — the drop the preview
        // promised. The caller's fit check rejects unsafe landings, so an
        // invalid hover no-ops.
        const cancelled = e.dataTransfer.dropEffect === "none";
        if (!cancelled && !dropHandledRef.current && hoverCell) {
          onDrop(id, hoverCell, grabOffset);
        }
        clear();
      },
    };
  }

  function dropHandlers(cell: DropCell): DropHandlers {
    return {
      onDragEnter: (e) => {
        if (!isCardDrag(e)) {
          return;
        }
        e.preventDefault();
        setHoverCell(cell);
      },
      onDragOver: (e) => {
        if (!isCardDrag(e)) {
          return;
        }
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
      },
      onDrop: (e) => {
        if (!isCardDrag(e)) {
          return;
        }
        e.preventDefault();
        dropHandledRef.current = true;
        const fromId = draggingId ?? e.dataTransfer.getData(CARD_DRAG_TYPE);
        if (fromId) {
          onDrop(fromId, cell, grabOffset);
        }
        clear();
      },
    };
  }

  return { draggingId, hoverCell, grabOffset, dragHandlers, dropHandlers };
}
